/**
 * Owner / Agency-Founder Command Center (Task 204) — pure presentation
 * logic, extracted from `OwnerCommandCenter.tsx` so it is unit-testable
 * without importing React. No fetching, no permission gating — display
 * mapping only. The FE gate (leadership) lives in `AppLanding`; RLS stays
 * the load-bearing security gate.
 */

import type { ClientRow, ClientStatus } from '../api/types';

/** The honest "no live data yet" glyph used across the command center. */
export const SOON = '—';

export function clientStatusLabelAr(status: ClientStatus): string {
  switch (status) {
    case 'active':
      return 'نشط';
    case 'onboarding':
      return 'قيد التأهيل';
    case 'paused':
      return 'متوقّف';
    case 'archived':
      return 'مؤرشف';
    default:
      return status;
  }
}

export function statusBadgeClass(status: ClientStatus): string {
  switch (status) {
    case 'active':
      return 'occ-b-sage';
    case 'onboarding':
      return 'occ-b-sky';
    case 'paused':
      return 'occ-b-rose';
    case 'archived':
      return 'occ-b-ghost';
    default:
      return 'occ-b-ghost';
  }
}

/**
 * Status → a conservative risk indicator. Honest heuristic (no risk model
 * exists yet): paused = high, onboarding = medium, active = low. Archived
 * carries no actionable risk.
 */
export function statusRisk(status: ClientStatus): { cls: string; label: string } {
  switch (status) {
    case 'paused':
      return { cls: 'hi', label: 'عالٍ' };
    case 'onboarding':
      return { cls: 'md', label: 'متوسط' };
    case 'active':
      return { cls: 'lo', label: 'منخفض' };
    default:
      return { cls: 'lo', label: '—' };
  }
}

/** Sort weight: needs-attention statuses float to the top of the table. */
export function statusWeight(status: ClientStatus): number {
  switch (status) {
    case 'paused':
      return 3;
    case 'onboarding':
      return 2;
    case 'active':
      return 1;
    default:
      return 0;
  }
}

export const AVATAR_COLORS = [
  'var(--tangerine)',
  'var(--plum)',
  'var(--sage)',
  'var(--sky)',
  '#B97309',
];

/** Deterministic avatar tint from a stable seed (the client id). */
export function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length] as string;
}

export function initial(name: string): string {
  return name.trim().charAt(0) || '؟';
}

/** Icons used by derived decisions — a subset of the component's icon set. */
export type DecisionIcon = 'pause' | 'user-plus';

export interface Decision {
  readonly key: string;
  readonly tone: string;
  readonly sev: 'urgent' | 'high' | 'mid';
  readonly sevLabel: string;
  readonly icon: DecisionIcon;
  readonly title: string;
  readonly why: string;
  readonly to: string;
  readonly action: string;
}

/**
 * Build the "decisions needed today" list from REAL client status only —
 * paused accounts (urgent review) and onboarding accounts (mid: finish
 * onboarding / assign an account manager). No fabricated signals; every
 * item links to the real client overview. Capped at 6.
 */
export function buildDecisions(clients: ReadonlyArray<ClientRow>): ReadonlyArray<Decision> {
  const out: Decision[] = [];
  for (const c of clients) {
    if (c.status === 'paused') {
      out.push({
        key: `paused-${c.id}`,
        tone: 'occ-dec-rose',
        sev: 'urgent',
        sevLabel: 'عاجل',
        icon: 'pause',
        title: `حساب متوقّف — ${c.name}`,
        why: 'العميل متوقّف حالياً؛ راجع الحساب وقرّر إعادة التفعيل أو الإغلاق قبل أن يتآكل الإيراد.',
        to: `/app/clients/${c.id}/overview`,
        action: 'فتح الحساب',
      });
    }
  }
  for (const c of clients) {
    if (c.status === 'onboarding') {
      out.push({
        key: `onb-${c.id}`,
        tone: 'occ-dec-plum',
        sev: 'mid',
        sevLabel: 'متوسط',
        icon: 'user-plus',
        title: `عميل جديد قيد التأهيل — ${c.name}`,
        why: 'أكمل التأهيل وأسند مدير حساب لتثبيت أهم أسبوعين في علاقة العميل.',
        to: `/app/clients/${c.id}/overview`,
        action: 'متابعة التأهيل',
      });
    }
  }
  return out.slice(0, 6);
}

const AR_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

/** Render Latin digits as Arabic-Indic numerals (UI display convention). */
export function toArabicDigits(n: number): string {
  return String(n)
    .split('')
    .map((ch) => (ch >= '0' && ch <= '9' ? AR_DIGITS[Number(ch)] : ch))
    .join('');
}
